import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isSpeechSynthesisSupported,
  lectureContentToSpeechText,
  pickPreferredVoice,
  splitSpeechChunks,
} from '../utils/lectureTts';

/**
 * Shared TTS controller for chat messages (one utterance queue at a time).
 */
export default function useChatMessageTts() {
  const supported = isSpeechSynthesisSupported();
  const [speakingId, setSpeakingId] = useState(null);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);
  const voiceRef = useRef(null);
  const activeIdRef = useRef(null);

  const loadVoice = useCallback(() => {
    if (!supported) return null;
    if (voiceRef.current) return voiceRef.current;
    const voices = window.speechSynthesis.getVoices() || [];
    voiceRef.current = pickPreferredVoice(voices);
    return voiceRef.current;
  }, [supported]);

  useEffect(() => {
    if (!supported) return undefined;
    loadVoice();
    const onVoices = () => {
      voiceRef.current = null;
      loadVoice();
    };
    window.speechSynthesis.addEventListener?.('voiceschanged', onVoices);
    window.speechSynthesis.getVoices();
    return () => {
      window.speechSynthesis.removeEventListener?.('voiceschanged', onVoices);
    };
  }, [supported, loadVoice]);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    activeIdRef.current = null;
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* ignore */
    }
    setSpeakingId(null);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const speak = useCallback(
    (id, rawText) => {
      if (!supported) {
        setError('Voice playback is not supported in this browser.');
        return;
      }
      const speakable = lectureContentToSpeechText(rawText);
      if (!speakable) {
        setError('Nothing to read in this message.');
        return;
      }

      // Toggle off if same message is already playing
      if (speakingId === id) {
        stop();
        return;
      }

      cancelledRef.current = false;
      setError(null);
      activeIdRef.current = id;
      setSpeakingId(id);

      const chunks = splitSpeechChunks(speakable);
      let index = 0;

      const speakChunk = () => {
        if (cancelledRef.current || activeIdRef.current !== id) return;
        if (index >= chunks.length) {
          setSpeakingId(null);
          activeIdRef.current = null;
          return;
        }

        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        utterance.rate = 0.95;
        utterance.pitch = 1.05;
        utterance.volume = 1;
        const voice = loadVoice();
        if (voice) utterance.voice = voice;
        utterance.lang = voice?.lang || 'en-US';

        utterance.onend = () => {
          if (cancelledRef.current || activeIdRef.current !== id) return;
          index += 1;
          speakChunk();
        };
        utterance.onerror = (event) => {
          if (cancelledRef.current || event?.error === 'interrupted' || event?.error === 'canceled') {
            return;
          }
          setSpeakingId(null);
          activeIdRef.current = null;
          setError('Could not play this message.');
        };

        try {
          window.speechSynthesis.resume();
          window.speechSynthesis.speak(utterance);
        } catch {
          setSpeakingId(null);
          activeIdRef.current = null;
          setError('Could not start voice playback.');
        }
      };

      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
      window.setTimeout(speakChunk, 40);
    },
    [supported, speakingId, stop, loadVoice]
  );

  return {
    supported,
    speakingId,
    error,
    speak,
    stop,
  };
}
